// Tiny "2 hours ago" formatter. Pure, deterministic given `now`, so tests
// don't need fake timers — just pass a fixed reference time.
//
// We intentionally don't pull in a date library for this. The bucketed
// rules below match the audit-timeline UX requirements (§7.5.3) and nothing
// else — anywhere that needs real locale-aware relative times can graduate
// to Intl.RelativeTimeFormat or date-fns without blocking this helper.
//
// Rules:
//   future    -> "just now"  (clock skew; treated as "moments ago")
//   < 60s     -> "just now"
//   < 60m     -> "N minute(s) ago"
//   < 24h     -> "N hour(s) ago"
//   < 30d     -> "N day(s) ago"
//   >= 30d    -> full locale date string (e.g. "23 Apr 2026")
//
// On invalid input (NaN parse), returns the raw input so nothing crashes
// and the broken value stays visible.

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function formatRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const delta = now.getTime() - then;

  if (delta < MINUTE_MS) return 'just now';
  if (delta < HOUR_MS) {
    const n = Math.floor(delta / MINUTE_MS);
    return `${n} minute${n === 1 ? '' : 's'} ago`;
  }
  if (delta < DAY_MS) {
    const n = Math.floor(delta / HOUR_MS);
    return `${n} hour${n === 1 ? '' : 's'} ago`;
  }
  if (delta < 30 * DAY_MS) {
    const n = Math.floor(delta / DAY_MS);
    return `${n} day${n === 1 ? '' : 's'} ago`;
  }
  return new Date(then).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
